import { forwardRef, Ref, useEffect, useImperativeHandle, useState } from 'react';
import { rankItem } from '@tanstack/match-sorter-utils';
import {
	ColumnDef,
	ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { useDeviceDetect } from '@utils';
import { RxCaretDown, RxCaretSort, RxCaretUp } from 'react-icons/rx';
import { PulseLoader } from 'react-spinners';

import type { FilterFn, Table as TableType } from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table/Table';

import styles from './dataTable.module.scss';

export interface DataTableRef {
	resetRowSelection: () => void;
}

interface DataTableProps<TData> {
	id?: string;
	data: TData[];
	columns: ColumnDef<TData>[];
	onRowSelectionChange?: (selectedRows: number[]) => void;
	enablePagination?: boolean;
	pageSize?: number;
	header?: (
		table: TableType<TData>,
		globalFilter: string,
		setGlobalFilter: (value: string) => void,
	) => React.ReactNode;
	footer?: (table: TableType<TData>) => React.ReactNode;
	isLoading?: boolean;
	isError?: boolean;
}

function DataTableInner<TData>(
	{
		id,
		data,
		columns,
		header,
		footer,
		onRowSelectionChange,
		enablePagination = false,
		pageSize = 15,
		isLoading = false,
		isError = false,
	}: DataTableProps<TData>,
	ref: React.Ref<DataTableRef>,
) {
	const { isDesktop } = useDeviceDetect();
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [globalFilter, setGlobalFilter] = useState('');
	const [rowSelection, setRowSelection] = useState({});
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		onGlobalFilterChange: setGlobalFilter,
		onColumnFiltersChange: setColumnFilters,
		enableMultiRowSelection: true,
		getFilteredRowModel: getFilteredRowModel(),
		onRowSelectionChange: setRowSelection,
		getSortedRowModel: getSortedRowModel(),
		onSortingChange: setSorting,
		state: {
			sorting,
			columnFilters,
			globalFilter,
			rowSelection,
		},
		filterFns: {
			fuzzy: fuzzyFilter,
		},
		...(enablePagination && {
			getPaginationRowModel: getPaginationRowModel(),
			initialState: {
				pagination: {
					pageIndex: 0,
					pageSize: pageSize,
				},
			},
		}),
	});

	useEffect(() => {
		if (onRowSelectionChange) {
			onRowSelectionChange(Object.keys(rowSelection).map(Number));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rowSelection]);

	useImperativeHandle(ref, () => ({
		resetRowSelection: () => setRowSelection({}),
	}));

	return (
		<div id={id}>
			<div>{header && header(table, globalFilter, setGlobalFilter)}</div>
			<div className={`${styles.tableContainer}`}>
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map(headerGroup => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map(header => {
									return (
										<TableHead
											key={header.id}
											style={{
												width: header.id === 'select' ? '1%' : header.getSize(),
												minWidth: header.id === 'select' ? '1%' : '125px',
												borderRight: header.id == 'select' ? '1px solid #EBEBEB' : '',
											}}
											className={`${styles.tableHead} ${
												header.id.includes('select') && table.getIsAllRowsSelected()
													? styles.selectedHeader
													: ''
											}`}
											colSpan={header.colSpan}
										>
											{header.isPlaceholder ? null : header.column.columnDef.enableSorting ? (
												<button
													className={styles.sortButton}
													onClick={header.column.getToggleSortingHandler()}
													title={
														header.column.getCanSort()
															? header.column.getNextSortingOrder() === 'asc'
																? 'Sort Ascending'
																: header.column.getNextSortingOrder() === 'desc'
																	? 'Sort Descending'
																	: 'Clear Sort'
															: undefined
													}
												>
													<span className={styles.headerContent}>
														{flexRender(header.column.columnDef.header, header.getContext())}
														{header.column.getIsSorted() ? (
															header.column.getIsSorted() === 'asc' ? (
																<RxCaretUp size={16} className={styles.sortIcon} />
															) : (
																<RxCaretDown size={16} className={styles.sortIcon} />
															)
														) : (
															<RxCaretSort size={16} className={styles.sortIcon} />
														)}
													</span>
												</button>
											) : (
												flexRender(header.column.columnDef.header, header.getContext())
											)}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{isLoading ? (
							<div className={styles.spinnerContainer}>
								<PulseLoader className={styles.spinner} />
							</div>
						) : table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row, index) => (
								<TableRow
									key={row.id}
									data-state={row.getIsSelected() && 'selected'}
									className={` ${row.getIsSelected() ? styles.selectedRow : ''} ${index % 2 == 0 ? styles.evenRow : ''} `}
									onMouseEnter={() => setHoveredRowIndex(index)}
									// TODO: Fix this on Safari - hovered row index is reset when popover is open
									// onMouseLeave={() => setHoveredRowIndex(null)}
								>
									{row.getVisibleCells().map(cell => {
										return (
											<TableCell
												key={cell.id}
												style={{
													width: cell.column.id === 'select' ? '1%' : cell.column.getSize(),
													minWidth: cell.column.id === 'select' ? '1%' : '125px',
													borderRight: cell.column.id == 'select' ? '1px solid #EBEBEB' : '',
												}}
												className={` ${styles.tableCell} ${
													cell.id.includes('select') && cell.row.getIsSelected()
														? styles.selectedCell
														: ''
												} `}
											>
												<div className={styles.cellContent}>
													{cell.id.includes('actions') && hoveredRowIndex !== index && isDesktop ? (
														<></>
													) : (
														flexRender(cell.column.columnDef.cell, cell.getContext())
													)}
												</div>
											</TableCell>
										);
									})}
								</TableRow>
							))
						) : isError ? (
							<TableRow>
								<TableCell colSpan={columns.length} className={styles.noResults}>
									Failed to fetch data, please try again later
								</TableCell>
							</TableRow>
						) : (
							<TableRow>
								<TableCell colSpan={columns.length} className={styles.noResults}>
									No results
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<div>{footer && footer(table)}</div>
		</div>
	);
}

const DataTable = forwardRef(DataTableInner) as <TData>(
	props: DataTableProps<TData> & { ref?: Ref<DataTableRef> },
) => ReturnType<typeof DataTableInner>;

const fuzzyFilter: FilterFn<unknown> = (row, columnId, value, addMeta) => {
	// Rank the item
	const itemRank = rankItem(row.getValue(columnId), value);

	// Store the itemRank info
	addMeta({
		itemRank,
	});

	// Return if the item should be filtered in/out
	return itemRank.passed;
};

export { DataTable };
